/** Подбор платья по фото клиента — запросы в группы салонов и ответы сотрудников
 * (`/api/chats/{id}/podbor`, `/api/deals/{id}/podbor`, `/api/leads/{id}/podbor`). */
import { apiFetch } from "@/lib/api";

export type PodborAnswer = {
  /** message_id ответа в группе Telegram. */
  id: number;
  date: string;
  author: string;
  text: string;
  /** Фото из ответа сотрудника; `null` — ответ без фото. */
  photo: string | null;
  /** Ответ на ответ коллеги: его `id` и автор; `null` — ответ на сам запрос. */
  reply: number | null;
  reply_author: string | null;
  /** Уровень в ветке реплаев: 0 — ответ на запрос. Список идёт веткой: ответ, под ним ответы на него. */
  depth: number;
};

export type Podbor = {
  id: number;
  chat: number;
  /** `forward` — в салон переслано сообщение клиента (`text`), `podbor` — фото на подбор,
   *  `question` — вопрос по заказу из поля ответа (режим «В салон»), ответы — комментариями в сделку. */
  kind: "podbor" | "forward" | "question";
  /** Что клиент хочет (окно «Переслать в салон» у молнии): подбор платья или сдать наряд. */
  type: "podbor" | "dress";
  type_name: string;
  /** Текст пересланного сообщения (у пачки — тексты по порядку); у подбора пусто. */
  text: string;
  /** Сколько сообщений переслано одним запросом; у подбора и вопроса — 0. */
  count: number;
  state: "new" | "sending" | "sent" | "answered" | "closed" | "cancelled" | "failed";
  state_name: string;
  /** Задача: `active` — «Активно», пока сотрудник не проверит и не отметит; `done` — «Завершено»;
   *  `null` — запрос в салон не уходил. */
  task: "active" | "done" | null;
  task_name: string;
  /** Кто и когда завершил задачу. */
  done_user: string;
  date_done: string | null;
  city: string | null;
  /** Вопрос сотрудникам салона; у пересланного сообщения — комментарий переславшего. */
  question: string;
  /** Группа Telegram, куда ушёл (или уйдёт) запрос. */
  group: string;
  deal: number | null;
  lead: number | null;
  /** Запрос подготовил AI вместе с ответом клиенту. */
  ai: boolean;
  /** Кто отправил запрос в группу. */
  user: string;
  error: string;
  date: string;
  date_answer: string | null;
  /** Фото клиента, ушедшие на подбор. */
  photos: string[];
  answers: PodborAnswer[];
};

type Rows = { rows: Podbor[] };

export const loadChatPodbor = (chatId: number) => apiFetch<Rows>(`/api/chats/${chatId}/podbor`);
export const loadDealPodbor = (dealId: number) => apiFetch<Rows>(`/api/deals/${dealId}/podbor`);
export const loadLeadPodbor = (leadId: number) => apiFetch<Rows>(`/api/leads/${leadId}/podbor`);

const act = (chatId: number, id: number, what: "send" | "cancel" | "done" | "reopen") =>
  apiFetch<{ podbor: Podbor }>(`/api/chats/${chatId}/podbor/${id}/${what}`, { method: "POST", body: "{}" });

/** Подготовленный запрос — в группу салона. */
export const sendPodbor = (chatId: number, id: number) => act(chatId, id, "send");
/** Неотправленный запрос больше не нужен. */
export const cancelPodbor = (chatId: number, id: number) => act(chatId, id, "cancel");
/** Задача сделана — «Завершено»; `done: false` — вернуть в «Активно». */
export const setPodborDone = (chatId: number, id: number, done: boolean) => act(chatId, id, done ? "done" : "reopen");

/** Сообщение в окне «Переслать в салон»: `file` — вложение не фото (в группу уйдёт пометкой). */
export type ForwardMessage = { id: number; text: string; out: boolean; photo: boolean; file: boolean; date: string };

/** Окно «Переслать в салон» (`GET /api/chats/{id}/forward?message=…`): что уйдёт и куда можно. */
export type ForwardForm = {
  message: ForwardMessage;
  /** Все пересылаемые сообщения по порядку переписки (пачка из режима выделения — больше одного). */
  messages: ForwardMessage[];
  /** Город чата, если у его салона есть группа; `null` — выбрать руками. */
  city: number | null;
  /** Города с салоном и группой Telegram «Общее <город>». */
  cities: { id: number; name: string; group: string }[];
  phone: number | null;
  deal: number | null;
  /** Карточка клиента в CRM (`sCustomer.id` последней примерки). */
  customer: number | null;
  /** Кто вёл последнюю примерку: его отметим в группе (`telegram` — знаем его Telegram). */
  stylist: { name: string; date: string; telegram: boolean } | null;
};

export const loadForward = (chatId: number, messageIds: number[]) =>
  apiFetch<ForwardForm>(`/api/chats/${chatId}/forward?${messageIds.map((id) => `message=${id}`).join("&")}`);

/** Сообщения клиента (одно или пачка) — одним запросом в группу салона города `city`; `text` —
 *  комментарий для салона. */
export const sendForward = (chatId: number, body: { messages: number[]; city: number; text: string }) =>
  apiFetch<{ podbor: Podbor }>(`/api/chats/${chatId}/forward`, { method: "POST", body: JSON.stringify(body) });
